<template>
  <EditorShell
    :items="shellItems"
    item-label="objective"
    :get-item-classes="objectiveItemClasses"
    :get-item-title="objectiveItemTitle"
    @add-click="startNewObjective"
    @item-click="onShellItemClick"
    @remove="onShellRemove"
  >
    <!-- Custom list row: name + token-count badge (no thumbnail) -->
    <template #list-item="{ item }">
      <span class="ve-list-name">{{ item.name }}</span>
      <span style="font-size:0.72rem;color:#8b949e;flex-shrink:0;margin-right:0.25rem">{{ item.sublabel }}</span>
    </template>

    <!-- Override entire right panel -->
    <template #detail>
      <!-- Inactive placeholder -->
      <div v-if="!newObjectiveActive" style="padding:3rem 1rem; text-align:center">
        <div style="font-size:2.5rem; margin-bottom:0.75rem">🎯</div>
        <p style="color:#8b949e; font-size:0.88rem">
          Click <strong style="color:#e6edf3">+ Add</strong> to create a new objective,<br>
          or click an existing objective to edit it.
        </p>
      </div>

      <!-- Active form: add or edit -->
      <div v-else class="new-objective-form">
        <div class="field-row" style="max-width:380px; margin-bottom:1rem">
          <label class="field-label">Objective Name</label>
          <input v-model="newObjectiveName" type="text" class="field-input" placeholder="e.g. Capture the Hill" />
        </div>

        <!-- Token palette -->
        <p class="field-label">Drag tokens onto the battlefield</p>
        <p v-if="availableTokenSkins.length === 0" class="field-desc" style="margin-top:0.3rem">
          No tokens selected — select tokens first in the Tokens section.
        </p>
        <div v-else class="obj-token-palette">
          <img
            v-for="skin in availableTokenSkins"
            :key="skin.id"
            :src="skin.thumbnailUrl"
            :alt="skin.name"
            :title="skin.name"
            class="obj-token-thumb"
            draggable="true"
            @dragstart="onObjectiveTokenDragStart(skin, $event)"
          />
        </div>

        <!-- Battlefield -->
        <div class="obj-battlefield-wrap">
          <div
            ref="battlefieldRect"
            class="obj-battlefield"
            :style="battlefieldRectStyle"
            @dragover.prevent="onObjectiveBfDragover($event)"
            @dragleave="onObjectiveBfDragLeave($event)"
            @drop.prevent="onObjectiveBfDrop($event)"
          >
            <div v-if="newObjectiveTokens.length === 0 && !objectiveDragPos" class="obj-bf-placeholder">
              Drop tokens here
            </div>
            <div
              v-if="objectiveDragPos"
              class="obj-coord-tooltip"
              :style="{ left: objectiveDragPos.labelLeft + 'px', top: objectiveDragPos.labelTop + 'px' }"
            >
              X:&nbsp;{{ objectiveDragPos.x.toFixed(1) }}"&nbsp;&nbsp;Y:&nbsp;{{ objectiveDragPos.y.toFixed(1) }}"
            </div>
            <div
              v-for="(t, ti) in newObjectiveTokens"
              :key="ti"
              class="obj-placed-token"
              :class="{ 'obj-placed-token--selected': selectedObjectiveTokenIndex === ti }"
              :style="{ left: ((t.x + bfWidth / 2) / bfWidth * 100) + '%', top: ((t.y + bfHeight / 2) / bfHeight * 100) + '%' }"
              :title="t.name"
              draggable="true"
              @dragstart.stop="onPlacedTokenDragStart(ti, $event)"
              @click.stop="selectObjectiveToken(ti)"
            >
              <img :src="t.thumbnailUrl" :alt="t.name" class="obj-placed-img" />
              <div class="obj-placed-coords">{{ t.x.toFixed(1) }}" {{ t.y.toFixed(1) }}"</div>
            </div>
          </div>
          <p class="field-desc" style="margin-top:0.35rem; text-align:center">
            Battlefield: {{ bfWidth }}" × {{ bfHeight }}"
            &nbsp;·&nbsp; {{ newObjectiveTokens.length }} token{{ newObjectiveTokens.length === 1 ? '' : 's' }} placed
            &nbsp;·&nbsp; Click to select · drag to reposition
          </p>
        </div>

        <!-- Selected placed token panel -->
        <div
          v-if="selectedObjectiveTokenIndex !== null && selectedObjectiveTokenIndex < newObjectiveTokens.length"
          class="obj-selected-panel"
        >
          <span class="obj-selected-name">{{ newObjectiveTokens[selectedObjectiveTokenIndex].name }}</span>
          <div class="obj-selected-fields">
            <label class="field-label">X"</label>
            <input
              type="number" step="0.1" class="field-input field-input-narrow"
              :value="newObjectiveTokens[selectedObjectiveTokenIndex].x"
              @change="$set(newObjectiveTokens[selectedObjectiveTokenIndex], 'x', parseFloat($event.target.value) || 0)"
            />
            <label class="field-label">Y"</label>
            <input
              type="number" step="0.1" class="field-input field-input-narrow"
              :value="newObjectiveTokens[selectedObjectiveTokenIndex].y"
              @change="$set(newObjectiveTokens[selectedObjectiveTokenIndex], 'y', parseFloat($event.target.value) || 0)"
            />
            <button
              class="obj-remove-token-btn" type="button" title="Remove token"
              @click="newObjectiveTokens.splice(selectedObjectiveTokenIndex, 1); selectedObjectiveTokenIndex = null"
            >&times;</button>
          </div>
        </div>
      </div>
    </template>
  </EditorShell>
</template>

<script>
import EditorShell from '@/components/EditorShell.vue';

export default {
  name: 'ObjectivesEditor',
  components: { EditorShell },

  props: {
    value: { type: Array, default: () => [] },
    availableTokenSkins: { type: Array, default: () => [] },
    battlefieldWidth: { type: Number, default: 72 },
    battlefieldHeight: { type: Number, default: 48 },
  },

  data() {
    return {
      objectives: [],
      newObjectiveActive: false,
      newObjectiveName: '',
      newObjectiveTokens: [],
      objectiveDragPos: null,
      selectedObjectiveTokenIndex: null,
      editingObjectiveIndex: null,
      editingObjectiveBackup: null,
    };
  },

  computed: {
    bfWidth() { return this.battlefieldWidth || 72; },
    bfHeight() { return this.battlefieldHeight || 48; },
    battlefieldRectStyle() {
      return { aspectRatio: `${this.bfWidth} / ${this.bfHeight}` };
    },
    shellItems() {
      return this.objectives.map((obj, oi) => ({
        id: oi,
        name: obj.name,
        sublabel: obj.tokens.length + '🪙',
      }));
    },
  },

  mounted() {
    if (this.value && this.value.length > 0) {
      this.initFromValue(this.value);
    }
  },

  watch: {
    value(newVal) {
      if (!this.newObjectiveActive) {
        this.initFromValue(newVal);
      }
    },
    availableTokenSkins(newSkins) {
      if (newSkins.length > 0 && this.value && this.value.length > 0) {
        this.initFromValue(this.value);
      }
    },
    newObjectiveName(val) {
      if (this.editingObjectiveIndex !== null) {
        this.$set(this.objectives, this.editingObjectiveIndex, {
          ...this.objectives[this.editingObjectiveIndex],
          name: val,
        });
        this.$emit('input', this.buildObjectivesJson());
      }
    },
    newObjectiveTokens: {
      handler(val) {
        if (this.editingObjectiveIndex !== null) {
          this.$set(this.objectives, this.editingObjectiveIndex, {
            ...this.objectives[this.editingObjectiveIndex],
            tokens: val.map(t => ({ ...t })),
          });
          this.$emit('input', this.buildObjectivesJson());
        }
      },
      deep: true,
    },
  },

  methods: {
    // ── EditorShell bridge ───────────────────────────────────────────────────

    objectiveItemClasses(item) {
      return {
        've-list-item--selected': this.newObjectiveActive && this.editingObjectiveIndex === item.id,
      };
    },

    objectiveItemTitle() {
      return '';
    },

    onShellItemClick(item, idx) {
      if (this.editingObjectiveIndex === idx) return;
      this.editObjective(idx);
    },

    onShellRemove(item, idx) {
      if (this.editingObjectiveIndex === idx) {
        // Closing edit mode for the removed item
        this.newObjectiveActive = false;
        this.newObjectiveName = '';
        this.newObjectiveTokens = [];
        this.editingObjectiveIndex = null;
        this.editingObjectiveBackup = null;
        this.selectedObjectiveTokenIndex = null;
      } else if (this.editingObjectiveIndex !== null && this.editingObjectiveIndex > idx) {
        this.editingObjectiveIndex--;
      }
      this.removeObjective(idx);
    },

    // ── Objective logic ──────────────────────────────────────────────────────

    initFromValue(json) {
      this.objectives = (json || []).map(obj => ({
        name: obj.Name,
        tokens: (obj.Items || obj.Tokens || []).map(t => {
          const skin = this.availableTokenSkins.find(s => String(s.id) === String(t.Id));
          return {
            id: String(t.Id),
            name: skin ? skin.name : String(t.Id),
            thumbnailUrl: skin ? skin.thumbnailUrl : '',
            x: parseFloat(t.PosX !== undefined ? t.PosX : (t.X || 0)),
            y: parseFloat(t.PosY !== undefined ? t.PosY : (t.Y || 0)),
          };
        }),
      }));
      // If an objective is currently open for editing, refresh its token list
      // so that newly resolved thumbnails are reflected on the battlefield
      if (this.newObjectiveActive && this.editingObjectiveIndex !== null) {
        const obj = this.objectives[this.editingObjectiveIndex];
        if (obj) {
          this.newObjectiveTokens = obj.tokens.map(t => ({ ...t }));
        }
      }
    },

    startNewObjective() {
      // Create and immediately start editing a new objective
      // (if already editing one, watchers have kept it saved, so we can safely move on)
      const newObjective = { name: 'New Objective', tokens: [] };
      this.objectives.push(newObjective);
      const newIdx = this.objectives.length - 1;
      this.editingObjectiveIndex = newIdx;
      this.editingObjectiveBackup = { name: 'New Objective', tokens: [] };
      this.newObjectiveName = newObjective.name;
      this.newObjectiveTokens = [];
      this.newObjectiveActive = true;
      this.selectedObjectiveTokenIndex = null;
      this.objectiveDragPos = null;
      this.$emit('input', this.buildObjectivesJson());
    },

    editObjective(oi) {
      const obj = this.objectives[oi];
      this.editingObjectiveIndex = oi;
      this.editingObjectiveBackup = { name: obj.name, tokens: obj.tokens.map(t => ({ ...t })) };
      this.newObjectiveName = obj.name;
      this.newObjectiveTokens = obj.tokens.map(t => ({ ...t }));
      this.newObjectiveActive = true;
      this.selectedObjectiveTokenIndex = null;
      this.objectiveDragPos = null;
    },

    removeObjective(oi) {
      this.objectives.splice(oi, 1);
      this.$emit('input', this.buildObjectivesJson());
    },

    selectObjectiveToken(ti) {
      this.selectedObjectiveTokenIndex = this.selectedObjectiveTokenIndex === ti ? null : ti;
    },

    onObjectiveTokenDragStart(skin, event) {
      event.dataTransfer.setData('text/plain', 'skin:' + String(skin.id));
      event.dataTransfer.effectAllowed = 'copy';
    },

    onPlacedTokenDragStart(ti, event) {
      event.dataTransfer.setData('text/plain', 'placed:' + ti);
      event.dataTransfer.effectAllowed = 'move';
      this.selectedObjectiveTokenIndex = null;
    },

    onObjectiveBfDragover(event) {
      const el = this.$refs.battlefieldRect;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const pxX = event.clientX - rect.left;
      const pxY = event.clientY - rect.top;
      const bw = this.bfWidth, bh = this.bfHeight;
      const x = Math.max(-bw / 2, Math.min(bw / 2, (pxX / rect.width) * bw - bw / 2));
      const y = Math.max(-bh / 2, Math.min(bh / 2, (pxY / rect.height) * bh - bh / 2));
      this.objectiveDragPos = {
        x, y,
        labelLeft: Math.min(pxX + 14, rect.width - 130),
        labelTop: Math.max(4, pxY - 30),
      };
    },

    onObjectiveBfDragLeave(event) {
      const el = this.$refs.battlefieldRect;
      if (el && !el.contains(event.relatedTarget)) {
        this.objectiveDragPos = null;
      }
    },

    onObjectiveBfDrop(event) {
      const data = event.dataTransfer.getData('text/plain');
      if (!this.objectiveDragPos) return;
      if (data.startsWith('placed:')) {
        const ti = parseInt(data.split(':')[1], 10);
        if (ti >= 0 && ti < this.newObjectiveTokens.length) {
          this.$set(this.newObjectiveTokens[ti], 'x', parseFloat(this.objectiveDragPos.x.toFixed(1)));
          this.$set(this.newObjectiveTokens[ti], 'y', parseFloat(this.objectiveDragPos.y.toFixed(1)));
        }
      } else {
        const skinId = data.startsWith('skin:') ? data.slice(5) : data;
        const skin = this.availableTokenSkins.find(s => String(s.id) === skinId);
        if (!skin) return;
        this.newObjectiveTokens.push({
          id: String(skin.id),
          name: skin.name,
          thumbnailUrl: skin.thumbnailUrl,
          x: parseFloat(this.objectiveDragPos.x.toFixed(1)),
          y: parseFloat(this.objectiveDragPos.y.toFixed(1)),
        });
      }
      this.objectiveDragPos = null;
    },

    buildObjectivesJson() {
      return this.objectives.map(obj => ({
        Name: obj.name,
        Items: obj.tokens.map(t => ({ Id: t.id, Type: 'Token', PosX: String(t.x), PosY: String(t.y) })),
      }));
    },
  },
};
</script>

<style>
.new-objective-form { background: transparent; }
.obj-token-palette { display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.4rem; margin-bottom: 0.75rem; }
.obj-token-thumb { width: 64px; height: 64px; object-fit: contain; border: 1px solid #30363d; border-radius: 6px; background: #21262d; cursor: grab; padding: 2px; transition: border-color 0.15s; }
.obj-token-thumb:hover { border-color: #58a6ff; }
.obj-token-thumb:active { cursor: grabbing; }
.obj-battlefield-wrap { margin-top: 0.75rem; display: flex; flex-direction: column; align-items: center; }
.obj-battlefield { position: relative; width: 100%; max-width: 600px; background: rgb(17, 30, 36); border: 3px solid #ffffff; border-radius: 4px; overflow: hidden; cursor: crosshair; }
.obj-bf-placeholder { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 0.85rem; color: #3a5a3a; pointer-events: none; user-select: none; }
.obj-coord-tooltip { position: absolute; background: rgba(0,0,0,0.78); color: #fff; font-size: 0.75rem; padding: 3px 8px; border-radius: 4px; pointer-events: none; white-space: nowrap; z-index: 10; }
.obj-placed-token { position: absolute; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; cursor: pointer; z-index: 5; user-select: none; }
.obj-placed-img { width: 32px; height: 32px; object-fit: contain; display: block; border-radius: 4px; border: 2px solid transparent; transition: border-color 0.15s; pointer-events: none; }
.obj-placed-token:hover .obj-placed-img { border-color: #58a6ff; }
.obj-placed-token--selected .obj-placed-img { border-color: #58a6ff; box-shadow: 0 0 0 2px rgba(88,166,255,0.4); }
.obj-placed-coords { font-size: 0.6rem; color: #fff; background: rgba(0,0,0,0.7); border-radius: 3px; padding: 1px 4px; white-space: nowrap; margin-top: 2px; pointer-events: none; line-height: 1.4; }
.obj-selected-panel { margin-top: 0.75rem; background: #1c2942; border: 1px solid #58a6ff; border-radius: 6px; padding: 0.55rem 0.85rem; display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.obj-selected-name { font-size: 0.88rem; color: #e6edf3; font-weight: 500; flex: 1; min-width: 80px; }
.obj-selected-fields { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.obj-remove-token-btn { width: 32px; height: 32px; min-width: 32px; display: flex; align-items: center; justify-content: center; background: #c0392b; border: none; border-radius: 4px; color: #fff; font-size: 1.1rem; line-height: 1; cursor: pointer; flex-shrink: 0; transition: background 0.15s; padding: 0; }
.obj-remove-token-btn:hover { background: #e74c3c; }
</style>
